import React, { forwardRef, useMemo } from 'react';

import { Experiences } from '@luxuryescapes/contract-svc-experience';

import FlatImageEditor from '~/components/Common/Images/FlatImageEditor';
import {
  FlatImageEditorHandle,
  FlatImageEditorPendingUploadState,
  FlatImageEditorProps,
  FlatImageItem,
  FlatImageUploadPayload,
} from '~/components/Common/Images/types';

type ExperienceCurationImagesProps = {
  items: Array<Experiences.Image>;
  handleImagesChange: (
    images: Array<Experiences.Image>,
    meta?: Parameters<NonNullable<FlatImageEditorProps['onItemsChange']>>[1],
  ) => void;
  handleSetImageHero: (imageId: number | string) => void;
  handleDeleteImage: (imageId: number | string) => void;
  handleUploadImage: (payload: FlatImageUploadPayload) => Promise<FlatImageItem>;
  imageHero: number | string | null;
  isDescriptionEditable: boolean;
  isUploadEnabled: boolean;
  onPendingUploadStateChange?: (state: FlatImageEditorPendingUploadState) => void;
};

const ExperienceCurationImages = forwardRef<FlatImageEditorHandle, ExperienceCurationImagesProps>(
  function ExperienceCurationImages(
    {
      items,
      handleImagesChange,
      handleSetImageHero,
      handleDeleteImage,
      handleUploadImage,
      imageHero,
      isDescriptionEditable,
      isUploadEnabled,
      onPendingUploadStateChange,
    },
    ref,
  ) {
    const flatItems = useMemo(
      () =>
        items.map((item, index) => ({
          id: item.id,
          description: item.description || '',
          descriptionEditable: isDescriptionEditable,
          displayId: String(item.id),
          isHero: imageHero != null ? String(imageHero) === String(item.id) : item.isHero,
          order: index + 1,
          previewUrl: item.urls[0]?.url,
        })),
      [imageHero, isDescriptionEditable, items],
    );

    return (
      <FlatImageEditor
        ref={ref}
        deleteActionLabel="Delete image"
        descriptionLabel="Short description"
        enablePendingCropUpload
        items={flatItems}
        onPendingUploadStateChange={onPendingUploadStateChange}
        showDeleteAction
        showDescriptionField={false}
        showHeroAction
        uploadDisabled={!isUploadEnabled}
        uploadLabel="Add images"
        onItemsChange={(nextItems, meta) => {
          handleImagesChange(
            nextItems.map((item) => {
              const currentItem = items.find((image) => String(image.id) === String(item.id));

              return {
                ...(currentItem || {
                  id: String(item.id),
                  urls: item.previewUrl
                    ? [
                        {
                          url: item.previewUrl,
                          height: 0,
                          width: 0,
                        },
                      ]
                    : [],
                }),
                description: item.description || null,
                isHero: item.isHero ?? false,
              } as Experiences.Image;
            }),
            meta,
          );
        }}
        onDelete={handleDeleteImage}
        onSetHero={handleSetImageHero}
        onUploadFile={isUploadEnabled ? handleUploadImage : undefined}
      />
    );
  },
);

export default ExperienceCurationImages;
